import { useEffect, useState } from 'react'
import { useAppState } from '../contexts/AppStateContext'
import { apiKeysApiService } from '../services/apiKeysApiService'
import { foldersApiService } from '../services/foldersApiService'
import { ApiError } from '../services/BaseApiService'
import type { ApiKey, Folder } from '../types'
import type { CreateApiKeyResponse } from '../services/apiKeysApiService'
import { colors, fontFamily, fontSize, radius, shadow, spacing, tracking } from '../styles/theme'
import { ConfirmModal, Select } from '../components/ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShield } from '@fortawesome/free-solid-svg-icons'
import { pathDepth } from '../utils/folderTree'

const CAPABILITY_OPTIONS: { value: 'render' | 'render_share'; label: string; description: string }[] = [
  {
    value: 'render',
    label: 'Render only',
    description: 'Render documents server-to-server (generate, preview, batch, connector pulls, warehouse renders). Can’t create share links or embed tokens.',
  },
  {
    value: 'render_share',
    label: 'Render + Share',
    description: 'Everything Render only can do, plus create shareable download links and mint embed tokens for browser/iframe use.',
  },
]

/** The stored value is a base ("render" / "render_share"), optionally followed by ",templates". */
export function capabilityLabel(capabilities: string): string {
  const parts = capabilities.split(',').map(p => p.trim())
  const base = parts.includes('render_share') ? 'render+share' : 'render'
  return parts.includes('templates') ? `${base}+templates` : base
}

export default function ApiKeysPage() {
  const { currentWorkspace } = useAppState()
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [newKeyName, setNewKeyName] = useState('')
  const [newKeyCaps, setNewKeyCaps] = useState<'render' | 'render_share'>('render')
  const [newKeyTemplates, setNewKeyTemplates] = useState(false)
  const [newKeyFolderId, setNewKeyFolderId] = useState('')
  const [folders, setFolders] = useState<Folder[]>([])
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<CreateApiKeyResponse | null>(null)
  const [error, setError] = useState('')
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null)
  const [revoking, setRevoking] = useState(false)

  async function load() {
    try {
      const data = await apiKeysApiService.list()
      setKeys(data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load API keys')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  // A key always belongs to the current workspace, so the folder choice is scoped to it too --
  // folders/list-all is already workspace-filtered server-side via the X-Workspace-Id header.
  useEffect(() => {
    if (!currentWorkspace) { setFolders([]); setNewKeyFolderId(''); return }
    foldersApiService.listAll().then(f => {
      setFolders(f)
      setNewKeyFolderId('')
    }).catch(() => {})
  }, [currentWorkspace])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setCreating(true)
    try {
      const res = await apiKeysApiService.create({
        name: newKeyName,
        capabilities: newKeyCaps,
        folderId: newKeyFolderId || undefined,
        // Live is read-only, so only a Test key may manage templates (the server refuses it otherwise).
        manageTemplates: !!currentWorkspace?.isTest && newKeyTemplates,
      })
      setCreated(res)
      setNewKeyName('')
      setNewKeyFolderId('')
      setNewKeyTemplates(false)
      load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create key')
    } finally {
      setCreating(false)
    }
  }

  async function handleConfirmRevoke() {
    if (!revokeTarget) return
    setError('')
    setRevoking(true)
    try {
      await apiKeysApiService.revoke(revokeTarget.apiKeyId)
      setRevokeTarget(null)
      load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Revoke failed')
      setRevokeTarget(null)
    } finally {
      setRevoking(false)
    }
  }

  const thStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    textAlign: 'left',
    fontSize: fontSize.sm,
    fontWeight: 600,
    color: colors.textSecondary,
    borderBottom: `1px solid ${colors.border}`,
    background: colors.background,
  }

  const tdStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: fontSize.base,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
  }

  const isTest = !!currentWorkspace?.isTest

  return (
    <div>
      <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, margin: 0, letterSpacing: tracking.tight, marginBottom: spacing.xl }}>
        API Keys
      </h1>

      {error && (
        <div style={{
          background: colors.errorLight, color: colors.error,
          border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>{error}</div>
      )}

      {created && (
        <div style={{
          background: colors.successLight,
          border: `1px solid ${colors.successBorder}`,
          borderRadius: radius.md,
          padding: spacing.md,
          marginBottom: spacing.md,
        }}>
          <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.success, marginBottom: spacing.xs }}>
            API key created — copy it now, it won't be shown again.
          </div>
          <code style={{
            display: 'block',
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.sm,
            padding: spacing.sm,
            fontSize: fontSize.sm,
            fontFamily: 'monospace',
            wordBreak: 'break-all',
          }}>
            {created.fullKey}
          </code>
          <button
            onClick={() => setCreated(null)}
            style={{
              marginTop: spacing.sm,
              padding: '4px 10px',
              background: 'none',
              border: `1px solid ${colors.border}`,
              borderRadius: radius.sm,
              fontSize: fontSize.sm,
              cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      <div style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: radius.md,
        padding: spacing.xl,
        marginBottom: spacing.lg,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md }}>
          <span style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>Creating for workspace:</span>
          <span style={{
            fontSize: fontSize.xs,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 99,
            background: isTest ? colors.infoLight : colors.successLight,
            color: isTest ? colors.info : colors.success,
          }}>
            {currentWorkspace ? (isTest ? 'Test' : 'Live') : '—'}
          </span>
          <span style={{ fontSize: fontSize.xs, color: colors.textMuted, fontFamily: fontFamily.mono }}>
            key will start with {isTest ? 'test_' : 'live_'}
          </span>
        </div>
        <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: spacing.md, maxWidth: 560 }}>
          <input
            type="text"
            value={newKeyName}
            onChange={e => setNewKeyName(e.target.value)}
            placeholder="Key name"
            required
            style={{
              padding: '8px 10px',
              border: `1px solid ${colors.border}`,
              borderRadius: radius.sm,
              fontSize: fontSize.base,
              outline: 'none',
            }}
          />

          <div>
            <div style={{ fontSize: fontSize.sm, fontWeight: 500, color: colors.text, marginBottom: spacing.xs }}>
              Capabilities
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
              {CAPABILITY_OPTIONS.map(opt => {
                const checked = newKeyCaps === opt.value
                return (
                  <label
                    key={opt.value}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: spacing.sm,
                      padding: spacing.sm,
                      border: `1px solid ${checked ? colors.primary : colors.border}`,
                      borderRadius: radius.sm,
                      background: checked ? colors.primaryLight : colors.surface,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="capabilities"
                      value={opt.value}
                      checked={checked}
                      onChange={() => setNewKeyCaps(opt.value)}
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <div style={{ fontSize: fontSize.base, fontWeight: 600, color: colors.text }}>{opt.label}</div>
                      <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: 2 }}>{opt.description}</div>
                    </div>
                  </label>
                )
              })}
              {isTest && (
                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: spacing.sm, padding: spacing.sm,
                  border: `1px solid ${newKeyTemplates ? colors.primary : colors.border}`, borderRadius: radius.sm,
                  background: newKeyTemplates ? colors.primaryLight : colors.surface, cursor: 'pointer',
                }}>
                  <input type="checkbox" checked={newKeyTemplates} onChange={e => setNewKeyTemplates(e.target.checked)} style={{ marginTop: 3 }} />
                  <div>
                    <div style={{ fontSize: fontSize.base, fontWeight: 600, color: colors.text }}>Also manage templates</div>
                    <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: 2 }}>
                      Upload, download and delete templates through the Docmosis-compatible API. Uploads become new versions in Test;
                      promote them to Live in DocMorphia. Test keys only.
                    </div>
                  </div>
                </label>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center' }}>
            {folders.length > 0 && (
              <div style={{ width: 240 }}>
                {/* Scoping to a folder limits the key to that folder and everything under it.
                    listAll() is path-ordered, so parents precede their children. */}
                <Select
                  placeholder="Entire workspace"
                  isClearable
                  isSearchable
                  value={newKeyFolderId || null}
                  onChange={v => setNewKeyFolderId(v ? String(v) : '')}
                  options={folders.map(f => ({
                    value: f.folderId,
                    label: (
                      <span style={{ paddingLeft: (pathDepth(f.path) - 1) * 12 }}>{f.name}</span>
                    ),
                  }))}
                />
              </div>
            )}
            <button
              type="submit"
              disabled={creating || !currentWorkspace}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 16px',
                background: colors.primary,
                color: colors.onPrimary,
                border: 'none',
                borderRadius: radius.sm,
                fontSize: fontSize.base,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <FontAwesomeIcon icon={faShield} style={{ width: 13 }} />
              {creating ? 'Creating...' : 'Create Key'}
            </button>
          </div>
        </form>
      </div>

      {loading ? (
        <div style={{ color: colors.textSecondary }}>Loading...</div>
      ) : keys.length === 0 ? (
        <div style={{ color: colors.textSecondary, fontSize: fontSize.base }}>No API keys yet.</div>
      ) : (
        <div style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: radius.md,
          boxShadow: shadow.sm,
          overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Prefix</th>
                <th style={thStyle}>Capabilities</th>
                <th style={thStyle}>Scope</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Last Used</th>
                <th style={thStyle}>Created</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {keys.map(k => (
                <tr key={k.apiKeyId}>
                  <td style={tdStyle}>{k.name}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: fontSize.sm }}>{k.keyPrefix}...</td>
                  <td style={tdStyle}>
                    <span style={{
                      fontSize: fontSize.xs,
                      fontWeight: 500,
                      color: capabilityLabel(k.capabilities) !== 'render' ? colors.warning : colors.textSecondary,
                      background: colors.background,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 4,
                      padding: '1px 6px',
                    }}>
                      {capabilityLabel(k.capabilities)}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, fontSize: fontSize.sm, color: colors.textSecondary }}>
                    {k.folderId
                      ? (folders.find(f => f.folderId === k.folderId)?.name ?? 'Folder')
                      : 'Entire workspace'}
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      color: k.isActive ? colors.success : colors.textSecondary,
                      fontSize: fontSize.sm,
                      fontWeight: 500,
                    }}>
                      {k.isActive ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, color: colors.textSecondary }}>
                    {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : 'Never'}
                  </td>
                  <td style={{ ...tdStyle, color: colors.textSecondary }}>
                    {new Date(k.createdAt).toLocaleDateString()}
                  </td>
                  <td style={tdStyle}>
                    {k.isActive && (
                      <button
                        onClick={() => setRevokeTarget(k)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 5,
                          padding: '3px 8px',
                          background: 'none',
                          border: `1px solid ${colors.errorBorder}`,
                          borderRadius: radius.sm,
                          fontSize: fontSize.sm,
                          cursor: 'pointer',
                          color: colors.error,
                        }}
                      >
                        <FontAwesomeIcon icon={faShield} style={{ width: 11 }} />
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        open={!!revokeTarget}
        title="Revoke API key"
        message={
          <>
            Revoke <strong>{revokeTarget?.name}</strong>? This cannot be undone — any integration
            using this key will immediately lose access.
          </>
        }
        confirmLabel="Revoke"
        danger
        loading={revoking}
        onConfirm={handleConfirmRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </div>
  )
}
